import React, { memo, useEffect } from "react";
import { Gift, User, UserGift } from "../../types";
import {
  Card,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  styled,
  Typography
} from '@material-ui/core';
import { useQueryParams, StringParam, withDefault } from "use-query-params";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store";
import { Box } from "@material-ui/system";
import { ProfileAvatar } from "../../components/UserPhoto/UserPhoto";
import { areEqual, FixedSizeList, ListChildComponentProps } from "react-window";
import timeAgo from "../../utils/timeAgo";
import { ReactWindowScroller } from "../../components/WindowScroller";
import { actions } from '../../services/userGifts';

const GiftItem: React.FC<ListChildComponentProps> = ({ data, index, style }) => {

  const sentGift = data.list[ index ] as UserGift;
  const gift = data.gifts[ sentGift.giftId ] as Gift;

  return (
    <Box style={style} sx={{ pb: 1 }}>
      <Stack direction="row" justifyContent="center">
        <Card variant="outlined" sx={{ padding: 1, width: '100%', maxWidth: 800, }}>
          <Stack direction="column" spacing={1} alignItems="center">
            <Stack direction="row" spacing={1} justifyContent="space-around" alignItems="center" sx={{ width: '100%' }}>
              <ProfileAvatar userId={sentGift.fromUserId} size={100} />
              <img src={`${process.env.REACT_APP_CDN_URL}/${gift.path}`} alt="Gift" width="80px" />
              <ProfileAvatar userId={sentGift.targetUserId} size={100} />
            </Stack>
            <Typography variant="caption">
              Status: {sentGift.status}
            </Typography>
            <Typography variant="caption">
              Created: {timeAgo(sentGift.createTime.toDate())}
            </Typography>
          </Stack>
        </Card>
      </Stack>
    </Box>
  );
};

const GiftItemMemo = memo(GiftItem, areEqual);


const Root = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  position: 'relative',
  flex: 1,
});

interface Props {
  user: User;
}

const UserGifts: React.FC<Props> = ({ user }) => {
  const dispatch = useDispatch();
  const state = useSelector((state: RootState) => state.userGifts);

  const [ filters, setFilters ] = useQueryParams({
    who: withDefault(StringParam, 'all'),
  });

  useEffect(() => {
    dispatch(actions.loadGifts(user.id));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ user.id ]);

  const handleFilterChange = (event: SelectChangeEvent<string>) => {
    setFilters({
      [ event.target.name ]: event.target.value
    }, 'replaceIn');
  };

  if (state.loading || !state.giftsSent[ user.id ]) {
    return (
      <Root>
        <CircularProgress />
      </Root>
    );
  }

  let list: UserGift[] = [ ...state.giftsSent[ user.id ] ];

  if (filters.who === 'fromMe') {
    list = state.giftsSent[ user.id ].filter(item => item.fromUserId === user.id);
  }
  else if (filters.who === 'toMe') {
    list = state.giftsSent[ user.id ].filter(item => item.targetUserId === user.id);
  }


  return (
    <Root>
      <Stack direction="column" spacing={1} alignItems="flex-start" sx={{ width: '100%' }}>
        <FormControl sx={{ minWidth: 120 }}>
          <InputLabel id="who-label">From Who</InputLabel>
          <Select
            name="who"
            labelId="type-label"
            value={filters.who || 'all'}
            label="From Who"
            onChange={handleFilterChange}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="fromMe">From me</MenuItem>
            <MenuItem value="toMe">To Me</MenuItem>
          </Select>
        </FormControl>
        <Divider flexItem />

        <ReactWindowScroller>
          {({ ref, outerRef, style, onScroll }) => (
            <FixedSizeList
              ref={ref}
              outerRef={outerRef}
              style={style}
              onScroll={onScroll}
              height={window.innerHeight}
              width="100%"
              itemCount={list.length}
              itemSize={180}
              itemData={{
                list: list,
                gifts: state.giftsData,
              }}
            >
              {GiftItemMemo}
            </FixedSizeList>
          )}
        </ReactWindowScroller>

      </Stack>
    </Root>
  );
};

export default UserGifts;